// The chrome every signed-in mobile screen sits inside: who you are at the top,
// whose system this is at the bottom.
//
// ── One wrapper, not twenty screen edits ───────────────────────────────────
// There are ~15 top-level screens, 4 tabs and 7 device screens, using three
// different app bars between them. Adding an identity line and a footer to each
// would be twenty edits that drift apart the first time somebody adds a screen
// and forgets. This renders once around the route outlet, so a new screen gets
// both for free and neither can be missing from one place only.
//
// ⚠️ IT DOES NOT WRAP THE PRE-AUTH SCREENS. Splash, Login, Sign-up, Forgot and
// Blocked all run BEFORE there is a user — there is no name, no id and no
// company to show, and a strip reading "User undefined" is worse than no strip.
// They stay outside the wrapper in MobileRoutes.
//
// ── Why it is a flex column and not two fixed bars ────────────────────────
// ⚠️ NINE PLACES SIZED THEMSELVES FROM `window.innerHeight - 64`, hard-coding
// the assumption that the only chrome is one 64px app bar — the relay tiles in
// TouchScreen, the dials in Fan/Light/Curtain/Phase, and BleDevicePanel. Adding
// a strip and a footer without telling them makes every one of those overflow
// and clip at the bottom, silently, because they never measure anything.
//
// So the content area is a real flex child (`flex:1, minHeight:0`) whose height
// is MEASURED and published through context. Those screens now ask what they
// actually have instead of guessing from the viewport, which also fixes them
// for the keyboard, split-screen and any future chrome.

import React, {
  createContext, useContext, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useApp } from '../../../core/state/AppContext';
import { APP_VERSION } from '../../../core/version';

// The measured height of the area a screen actually gets. Defaults to the
// viewport so anything rendered OUTSIDE the shell — the desktop panel reusing a
// mobile control, a screen under test — keeps its old behaviour instead of
// collapsing to zero.
const ContentHeightContext = createContext(null);

/**
 * The height available to the current screen, in px.
 *
 * ⚠️ USE THIS INSTEAD OF `window.innerHeight`. The window includes the identity
 * strip and the footer, which the screen does not get. Callers still subtract
 * their own app bar — that is inside the content area and theirs to account for.
 */
export function useContentHeight() {
  const measured = useContext(ContentHeightContext);
  const [fallback, setFallback] = useState(() => (typeof window === 'undefined' ? 800 : window.innerHeight));
  useEffect(() => {
    if (measured != null) return undefined;
    const onResize = () => setFallback(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measured]);
  return measured != null ? measured : fallback;
}

// ── Top: who is signed in ─────────────────────────────────────────────────
// Deliberately thin. Every screen below still carries its own title and its own
// back button; this answers a different question ("as whom am I doing this"),
// which matters most when an installer is holding somebody else's account.
function IdentityStrip() {
  const theme = useTheme();
  const c = theme.sli;
  const { user, userId, realUserId, viewAsUser } = useApp();

  const name = [user?.Surname, user?.Name, user?.FatherHusband]
    .filter(Boolean).join(' ').trim() || `User ${userId ?? ''}`.trim();

  // ⚠️ A Super Admin can drive the app AS someone else, and then `userId` is
  // not `realUserId`. Showing the effective id alone would have support chasing
  // the wrong account; showing the real one alone would misexplain what the
  // screen is doing. So the effective id is shown, and marked when borrowed.
  const borrowed = viewAsUser && String(userId) !== String(realUserId);

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.375,
        bgcolor: c.button,
        color: c.text,
        borderBottom: `1px solid ${c.text}22`,
      }}
    >
      <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, minWidth: 0 }}>
        {name}
      </Typography>
      <Typography sx={{ fontSize: 11, opacity: 0.85, whiteSpace: 'nowrap' }}>
        {`· ID ${userId ?? '—'}`}
      </Typography>
      {borrowed && (
        <Typography
          sx={{
            fontSize: 10, fontWeight: 700, px: 0.5, borderRadius: 1,
            bgcolor: '#FFB74D33', color: '#FFCC80', whiteSpace: 'nowrap',
          }}
        >
          {`viewing as · you are ${realUserId}`}
        </Typography>
      )}
    </Box>
  );
}

// ── Bottom: whose system this is ──────────────────────────────────────────
// Below the tab bar on Home, not above it: the tabs are navigation and belong
// against the thumb, the company line is a signature and belongs under
// everything.
function CompanyFooter() {
  const theme = useTheme();
  const c = theme.sli;
  const { companyName } = useApp();

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: 1,
        py: 0.375,
        bgcolor: c.button,
        color: c.text,
        borderTop: `1px solid ${c.text}22`,
      }}
    >
      {companyName && <BusinessIcon sx={{ fontSize: 13, opacity: 0.9 }} />}
      <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, minWidth: 0 }}>
        {companyName || ''}
      </Typography>
      {/* Moved down from the app bar, where it sat as a 9px overlay in the
          corner. It is a support detail, and this is where support details go. */}
      {APP_VERSION && (
        <Typography sx={{ fontSize: 10, opacity: 0.6, whiteSpace: 'nowrap' }}>
          {`v${APP_VERSION}`}
        </Typography>
      )}
    </Box>
  );
}

export default function MobileShell({ children }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);

  // Measured, not computed from constants — the strip and footer are text and
  // will not stay the height I happen to give them today.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => setHeight(el.clientHeight);
    read();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <IdentityStrip />
      {/* ⚠️ `minHeight: 0` IS LOAD-BEARING. Without it a flex child refuses to
          shrink below its content, so a long screen pushes the footer off the
          bottom instead of scrolling inside — the classic flexbox trap. */}
      <Box ref={ref} sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <ContentHeightContext.Provider value={height}>
          {children}
        </ContentHeightContext.Provider>
      </Box>
      <CompanyFooter />
    </Box>
  );
}
